import { fetchJSON, querySelectorAll } from '../helpers'

declare global {
  interface Window {
    glossary: any
    Tooltip: any
  }
}

interface Glossary {
  Term: string
  Definition: string
}

interface GlossaryResponse {
  totalSize: number
  items: Glossary[]
}

function updateTextNodes(root: HTMLElement, cb: (node: any) => void) {
  const nodes = []
  const walk = document.createNodeIterator(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n: any) => {
      return n.data.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    },
  })

  let node
  while ((node = walk.nextNode())) {
    nodes.push(node)
  }

  nodes.forEach(n => {
    cb(n)
  })
}

function getKeys(terms: Element[]) {
  const keys = new Set<string>()
  terms.forEach(el => {
    const key = el.textContent!.toLowerCase()
    el.setAttribute(
      'href',
      `${location.pathname}#definition-${key.replace(/[^\w]/g, '')}`
    )
    if (key) {
      keys.add(key)
    }
  })
  const keysArray: string[] = []
  keys.forEach(v => keysArray.push(v))
  return keysArray.sort()
}

function createAnchor(key: string) {
  const anchor = document.createElement('a')
  anchor.className = 'glossary_term'
  anchor.id = key.toLowerCase().replace(/[^\w]/g, '')
  return anchor
}

function createTerm(key: string) {
  const term = document.createElement('dt')
  term.className = 'glossary_term'
  term.textContent = key
  return term
}

function createDefinition(
  key: string,
  dictionary: { [index: string]: string }
) {
  const definition = document.createElement('dd')
  definition.className = 'glossary_definition'
  definition.textContent = dictionary[key]
  return definition
}

function addToolTip(
  link: Element,
  dictionary: { [index: string]: string },
  container: Element
) {
  const key = link.textContent!.toLowerCase()
  const tip = new window.Tooltip(link, {
    title: dictionary[key],
    delay: { show: 400, hide: 100 },
    container,
  })
}

function addGlossaryTerms(
  container: Element,
  dictionary: { [index: string]: string },
  parent: Element
) {
  const includedTerms = querySelectorAll('.glossary-tag', document)
  const includedKeys = getKeys(includedTerms)

  includedKeys.forEach(key => {
    const anchor = createAnchor(key)
    container.appendChild(anchor)
    anchor.appendChild(createTerm(key))
    container.appendChild(createDefinition(key, dictionary))
  })

  const glossaryToggle: HTMLInputElement = document.getElementById(
    'glossary-toggle'
  ) as any

  if (glossaryToggle) {
    includedTerms.forEach(link => {
      if (!link.textContent) {
        link.remove()
        return
      }
      addToolTip(link, dictionary, parent)

      const href = link.getAttribute('href')
      if (href) {
        const id = href.split('#definition-').pop()
        if (id) {
          const anchor = document.getElementById(id)
          if (anchor) {
            link.addEventListener('click', (e: Event) => {
              e.preventDefault()
              glossaryToggle.checked = true
              window.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: anchor.getBoundingClientRect().top + window.scrollY - 10,
              })
            })
          }
        }
      }
    })
  }
}

export async function initializeGlossary(selector: string) {
  const glossaryContainer = document.querySelector('.glossary_container')
  if (!glossaryContainer || window.glossary) {
    return
  }

  try {
    const { items } = await fetchJSON<GlossaryResponse>(
      '/api/v1/WAIRC-Glossary?fields=Term,Definition'
    )

    const main = document.querySelector(selector) as HTMLElement
    const glossary = items.reduce((collection, { Term, Definition }) => {
      return { ...collection, [Term.toLowerCase()]: Definition }
    }, {})

    const terms = Object.keys(glossary)
    window.glossary = glossary

    if (main) {
      updateTextNodes(main, node => {
        try {
          const re = new RegExp(`\\b(${terms.join('|')})\\b`, 'i')
          if (re.test(node.data)) {
            if (node.parentNode.tagName !== 'A') {
              let remainingString = node.data
              interface GlossaryMatch {
                text: string
                match: string | null
              }
              const matchedParts: GlossaryMatch[] = []
              while (remainingString.length) {
                const replacementParts = re.exec(remainingString)
                if (replacementParts) {
                  matchedParts.push({
                    text: remainingString.substr(0, replacementParts.index),
                    match: replacementParts[0],
                  })
                  remainingString = remainingString.substr(
                    replacementParts.index + replacementParts[0].length
                  )
                } else {
                  matchedParts.push({
                    text: remainingString,
                    match: null,
                  })
                  remainingString = ''
                }
                re.lastIndex = 0
              }
              const newNode = document.createDocumentFragment()
              matchedParts.forEach(matchObj => {
                newNode.appendChild(document.createTextNode(matchObj.text))
                if (matchObj.match) {
                  const anchor = document.createElement('a')
                  anchor.className = 'glossary-tag'
                  anchor.text = matchObj.match
                  newNode.appendChild(anchor)
                }
              })
              node.replaceWith(newNode)
            }
          }
        } catch (error) {
          console.error(error)
        }
      })
    }

    addGlossaryTerms(glossaryContainer, glossary, main)
  } catch (error) {
    console.error('Error initialising glossary', error)
  }
}
